import React, { useEffect, useState } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Snackbar, Alert,
} from '@mui/material';
import { Add as AddIcon, Straighten, Refresh, DeleteOutline } from '@mui/icons-material';
import {
  PageHeader, StatCard, TableSurface, headerCellSx, bodyRowSx, TableEmptyState, ErrorState, TypePill,
} from '../../components/common/pageKit';
import { uomService, Uom } from '../../services/uomService';

const CATEGORIES = ['COUNT', 'WEIGHT', 'LENGTH', 'VOLUME', 'AREA', 'TIME', 'OTHER'];

const UomMasterPage: React.FC = () => {
  const [uoms, setUoms] = useState<Uom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ uomCode: '', uomName: '', uomCategory: 'COUNT' });

  const load = () => {
    setLoading(true);
    uomService.getUoms()
      .then((r) => { setUoms(r.uoms || []); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load UOMs'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.uomCode.trim() || !form.uomName.trim()) { setToast('Code and name are required'); return; }
    try {
      await uomService.createUom(form);
      setOpen(false); setForm({ uomCode: '', uomName: '', uomCategory: 'COUNT' });
      setToast('UOM created'); load();
    } catch (e: any) { setToast(e.message || 'Failed to create'); }
  };

  const remove = async (u: Uom) => {
    try { await uomService.deleteUom(u.id); setToast('UOM deleted'); load(); }
    catch (e: any) { setToast(e.message || 'Failed to delete'); }
  };

  return (
    <Box>
      <PageHeader
        title="Unit of Measure Master"
        subtitle="The single source of UOMs used across products, purchasing, inventory and sales."
        actions={
          <>
            <IconButton onClick={load} disabled={loading}><Refresh /></IconButton>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Add UOM</Button>
          </>
        }
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard title="Total UOMs" value={loading ? null : uoms.length} icon={<Straighten />} color="#2563eb" />
        <StatCard title="Categories" value={loading ? null : new Set(uoms.map((u) => u.uom_category)).size} icon={<Straighten />} color="#7c3aed" />
        <StatCard title="Active" value={loading ? null : uoms.filter((u) => u.is_active !== 0).length} icon={<Straighten />} color="#059669" />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableSurface>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>{['Code', 'Name', 'Category', ''].map((h) => <TableCell key={h} sx={headerCellSx}>{h}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 6, border: 0 }}><CircularProgress size={26} /></TableCell></TableRow>
              ) : !uoms.length ? (
                <TableRow><TableCell colSpan={4} sx={{ border: 0 }}><TableEmptyState title="No UOMs" hint="Add your first unit of measure." /></TableCell></TableRow>
              ) : uoms.map((u) => (
                <TableRow key={u.id} sx={bodyRowSx} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{u.uom_code}</TableCell>
                  <TableCell>{u.uom_name}</TableCell>
                  <TableCell><TypePill label={u.uom_category} tint="#0891b2" /></TableCell>
                  <TableCell align="right"><IconButton size="small" onClick={() => remove(u)} title="Delete"><DeleteOutline fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Add Unit of Measure</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField size="small" label="Code" value={form.uomCode} onChange={(e) => setForm({ ...form, uomCode: e.target.value.toUpperCase() })} autoFocus />
            <TextField size="small" label="Name" value={form.uomName} onChange={(e) => setForm({ ...form, uomName: e.target.value })} />
            <TextField select size="small" label="Category" value={form.uomCategory} onChange={(e) => setForm({ ...form, uomCategory: e.target.value })}>
              {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" onClick={create}>Create</Button></DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2600} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

export default UomMasterPage;
